// src/components/home/Home.js
import React, { useEffect, useState, useContext } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Settings } from 'lucide-react';
import Navbar from '../navbar/Navbar';
import { useNavigate } from 'react-router-dom';
import './home.css';
import { AppContext } from '../../context/AppContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../api';

const Home = () => {
  const [activeTab, setActiveTab] = useState('qr');
  const [isQrCodeFlow, setIsQrCodeFlow] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const navigate = useNavigate();

  const { registerDevice } = useContext(AppContext);

  // --------- Registration Success Redirect Handler ---------
  useEffect(() => {
    const handleRegistrationSuccess = async () => {
      try {
        const hash = window.location.hash;
        console.log('📍 handleRegistrationSuccess: Full hash:', hash);

        // Extract query string from hash (handles both #?params and #/path?params)
        let queryString = '';
        if (hash.includes('?')) {
          queryString = hash.split('?')[1];
        }

        console.log('📍 handleRegistrationSuccess: Query string:', queryString);

        const urlParams = new URLSearchParams(queryString);
        const registrationSuccess = urlParams.get('registration_success') === 'true';
        const device_id = urlParams.get('device_id');

        console.log('📍 handleRegistrationSuccess: registrationSuccess=', registrationSuccess);
        console.log('📍 handleRegistrationSuccess: device_id=', device_id);

        if (!registrationSuccess || !device_id) {
          console.log('❌ No registration success parameters found in URL');
          return; // not a registration success redirect
        }

        console.log('🔄 Registration success redirect detected for device:', device_id);
        setIsQrCodeFlow(true);

        // Verify device registration by fetching its status
        const response = await fetch(
          `${api}/device-name/${device_id}`,
          { signal: AbortSignal.timeout(5000) }
        );

        if (!response.ok) {
          console.error('Failed to verify device registration');
          toast.error('Failed to verify device registration. Please try again.');
          setIsQrCodeFlow(false);
          return;
        }

        const data = await response.json();
        console.log('✅ Device registration verified:', data);

        // Update localStorage with registration data
        if (data.store_id) localStorage.setItem('store_id', data.store_id);
        if (data.deck_id) localStorage.setItem('deck_id_for_polling', data.deck_id);
        localStorage.setItem('device_id', device_id);

        // Clean up URL
        window.history.replaceState(null, '', window.location.origin + window.location.pathname);

        // Fetch deck and related data
        const regResult = await registerDevice({ device_id, store_id: data.store_id });
        console.log('Registration and data fetch completed:', regResult);

        const qrMode = localStorage.getItem('qr_mode') || 'feedback';
        if (qrMode === 'polling') {
          navigate('/polling');
        } else {
          navigate('/question1');
        }
      } catch (error) {
        console.error('Registration success handler error:', error);
        toast.error('An error occurred during registration. Please try again.');
        setIsQrCodeFlow(false);
      }
    };

    handleRegistrationSuccess();
  }, [navigate, registerDevice]);

  // --------- QR Code Flow Detection & Processing ---------
  useEffect(() => {
    const handleQrCodeFlow = async () => {
      try {
        const hash = window.location.hash;
        if (!hash.includes('device_type=')) return false; // not a QR flow

        setIsQrCodeFlow(true);

        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const urlParams = new URLSearchParams(queryString);

        const device_type = urlParams.get('device_type')?.trim();
        const device_name = urlParams.get('device_name')?.trim();
        const device_id = urlParams.get('device_id')?.trim();
        const store_id = urlParams.get('store_id')?.trim();
        const business_id = urlParams.get('business_id')?.trim();
        const deck_id = urlParams.get('deck_id')?.trim();
        const mode = urlParams.get('mode')?.trim() || 'feedback'; // Default to feedback if not specified

        if (!device_id || !store_id || !device_type) {
          throw new Error('Invalid QR code. Contact support.');
        }

        // Call unified registerDevice
        const res = await registerDevice({
          device_id,
          device_name,
          store_setup_code: null,
          store_id: store_id,
          business_id: business_id
        });

        console.log('Device registration response:', res);

        // Check if registration was successful
        // The backend returns status: 'success' on success, or status: 'error' on failure
        if (res && (res.status === 'success' || res.status === 'pending')) {
          setDeviceId(device_id);

          // Store QR parameters in localStorage for the target component
          if (deck_id) {
            localStorage.setItem('deck_id_for_polling', deck_id);
          }
          localStorage.setItem('device_id', device_id);
          localStorage.setItem('device_type', device_type);
          localStorage.setItem('qr_mode', mode);
          localStorage.setItem('store_id', store_id);
          localStorage.setItem('business_id', business_id);

          console.log(`✅ Device registered successfully. Navigating to ${mode === 'polling' ? '/polling' : '/question1'}`);

          window.history.replaceState(null, '', '/');

          // Route based on mode
          if (mode === 'polling') {
            navigate('/polling');
          } else {
            navigate('/question1');
          }
        } else {
          console.error('Registration failed with response:', res);
          const errorMsg = res?.message || res?.error || 'Failed to register device via QR';
          throw new Error(errorMsg);
        }

        return true;
      } catch (error) {
        console.error('QR Code registration error:', error);
        toast.error(error.message);
        setIsQrCodeFlow(false);
        return false;
      }
    };

    handleQrCodeFlow();
  }, [navigate, registerDevice]);

  // --------- Manual Flow Initialization ---------
  useEffect(() => {
    if (isQrCodeFlow) return; // skip if QR code flow

    const initManualDevice = async () => {
      try {
        const storedDeviceId = localStorage.getItem('device_id');
        if (storedDeviceId) {
          setDeviceId(storedDeviceId);
          return;
        }

        // Generate temporary device_id if not present
        const tempDeviceId = Math.random().toString(36).substring(2, 18);
        localStorage.setItem('device_id', tempDeviceId);
        setDeviceId(tempDeviceId);
      } catch (error) {
        console.error('Manual flow init error:', error);
      }
    };

    initManualDevice();
  }, [isQrCodeFlow]);

  // --------- Auto-Navigate if already registered ---------
  useEffect(() => {
    if (isQrCodeFlow) return; // Skip if QR code flow is in progress

    const storedDeviceId = localStorage.getItem('device_id');
    const storedStoreId = localStorage.getItem('store_id');
    const qrMode = localStorage.getItem('qr_mode');

    // If qr_mode is set, let the QR handler navigate instead
    if (qrMode) return;

    if (storedDeviceId && storedDeviceId !== 'null' && storedStoreId && storedStoreId !== 'null') {
      // Navigate to question1 for manual flows (no qr_mode set)
      navigate('/question1');
    }
  }, [navigate, isQrCodeFlow]);

// --------- Polling for Device Registration Status ---------
  useEffect(() => {
    // Skip polling if QR code flow or no device ID
    if (!deviceId || isQrCodeFlow ) return;

    const interval = setInterval(() => {
      fetch(`${api}/check-device/${deviceId}`)
        .then(res => res.json())
        .then(data => {
          if (data.registered) {
            clearInterval(interval);
            localStorage.setItem('device_id', deviceId);
            localStorage.setItem('store_id', data.store_id);

            // store the device ID and store ID in indexedDB also
            const dbRequest = indexedDB.open('FDBK', 1);
            dbRequest.onupgradeneeded = (event) => {
              const db = event.target.result;
              if (!db.objectStoreNames.contains('devices')) {
                db.createObjectStore('devices', { keyPath: 'device_id' });
              }
            };
            dbRequest.onsuccess = (event) => {
              const db = event.target.result;
              const transaction = db.transaction('devices', 'readwrite');
              const store = transaction.objectStore('devices');
              store.put({ device_id: deviceId, store_id: data.store_id });
            };
            dbRequest.onerror = (event) => {
              console.error('IndexedDB error:', event.target.error);
            };

            navigate('/question1');
          }
        })
        .catch(console.error);
    }, 60000);

    return () => clearInterval(interval);
  }, [navigate, deviceId, isQrCodeFlow]);

  // Generate QR code URL with tablet_url for redirect after registration
  const tabletUrl = window.location.origin + window.location.pathname;
  const loginUrl = `https://dashboard.fdbkinsights.com/login?device_id=${encodeURIComponent(deviceId)}&tablet_url=${encodeURIComponent(tabletUrl)}`;

  if (isQrCodeFlow) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Processing QR Code...</h2>
            <p className="text-gray-600">Please wait while we set up your device.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2">Welcome to FDBK!</h1>
            <p className="text-lg text-gray-600 mb-4">Register your device</p>
          </div>

          {deviceId && (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('qr')}
                  className={`flex-1 py-4 font-semibold transition-all ${
                    activeTab === 'qr'
                      ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  Scan QR Code
                </button>
                <button
                  onClick={() => setActiveTab('manual')}
                  className={`flex-1 py-4 font-semibold transition-all ${
                    activeTab === 'manual'
                      ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  Manual Registration
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-8">
                {activeTab === 'qr' ? (
                  <div className="text-center">
                    <div className="inline-block p-6 bg-white rounded-xl shadow-lg mb-4">
                      <QRCodeSVG
                        value={loginUrl}
                        size={window.innerWidth < 640 ? 180 : window.innerWidth < 1024 ? 220 : 200}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        className="rounded-lg"
                      />
                    </div>
                    <p className="text-gray-600">Use your device camera to scan the QR code above</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div
                      className="inline-flex items-center justify-center w-48 h-48 bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl shadow-lg cursor-pointer hover:shadow-xl transition-transform transform hover:-translate-y-1 mb-4"
                      onClick={() => navigate('/config')}
                    >
                      <Settings className="w-20 h-20 text-white" />
                    </div>
                    <p className="text-gray-600">Click the settings icon above to manually register your device</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;
